import { useCallback, useRef } from 'react';
import BackendService from '../../../services/admin/BackendService';
import { InvestorStatementItem } from '../../../models/admin/InvestorStatement';

type ToastFn = (message: string, type: 'error' | 'warning' | 'info' | 'success') => void;
type UpdateItemFn = (externalId: string, patch: Partial<InvestorStatementItem>) => void;

function triggerBrowserDownload(url: string, filename: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * View/skip actions for the statement review list — kept together because
 * both read/write the same "has this admin already seen the file" tracking
 * (viewedIdsRef): skip only force-downloads when the item was never viewed
 * first, since an admin who already opened it has a copy either way.
 */
export function useStatementViewAndSkipActions(
  backendRef: React.MutableRefObject<BackendService>,
  updateItem: UpdateItemFn,
  setBusyItemId: (id: string | null) => void,
  onShowToast: ToastFn,
) {
  const viewedIdsRef = useRef<Set<string>>(new Set());

  const handleView = useCallback(
    async (item: InvestorStatementItem) => {
      setBusyItemId(item.externalId);
      try {
        const url = await backendRef.current.getInvestorStatementViewUrl(item.externalId);
        viewedIdsRef.current.add(item.externalId);
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err: unknown) {
        onShowToast(err instanceof Error ? err.message : 'Failed to open statement', 'error');
      } finally {
        setBusyItemId(null);
      }
    },
    [backendRef, onShowToast, setBusyItemId],
  );

  const handleSkip = useCallback(
    async (item: InvestorStatementItem) => {
      setBusyItemId(item.externalId);
      try {
        const { forceDownloadUrl } = await backendRef.current.discardInvestorStatement(
          item.externalId,
        );
        if (forceDownloadUrl && !viewedIdsRef.current.has(item.externalId)) {
          triggerBrowserDownload(
            forceDownloadUrl,
            `${item.investmentEntityName || 'statement'}.docx`,
          );
        }
        updateItem(item.externalId, { status: 'DISCARDED' });
      } catch (err: unknown) {
        onShowToast(err instanceof Error ? err.message : 'Failed to skip statement', 'error');
      } finally {
        setBusyItemId(null);
      }
    },
    [backendRef, onShowToast, setBusyItemId, updateItem],
  );

  return { handleView, handleSkip };
}
